import { useMemo, useState } from 'react';
import {
  Eye,
  ArrowRight,
  FileSpreadsheet,
  Ship,
  Plane,
  Truck,
  Building2,
  User,
  Search,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { QuotePdfButton } from './quote-pdf-button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils/cn';
import { quoteValidity, VALIDITY_LABEL, type QuoteValidityState } from '../utils/quote-validity';
import { formatShortDate } from '../utils/trade-dates';
import {
  OUTCOME_LABEL,
  OUTCOME_TONE,
  LOST_REASON_LABEL,
  effectiveOutcome,
  type QuoteOutcome,
} from '../utils/quote-outcome';
import { QuoteActions } from './quote-actions';
import type { ImportOrder } from '@/features/import-tracking/types/import-tracking.types';

/**
 * Lista de cotizaciones de importación.
 *
 * Reemplaza la tabla anterior, que estaba armada para otro concepto: mostraba
 * FOB (que en una cotización de flete todavía no existe), no mostraba la ruta y
 * —lo más grave— no mostraba la **vigencia**, que es el dato que decide si la
 * cotización se puede prometer o hay que recotizar.
 *
 * El orden de las columnas sigue lo que el vendedor pregunta:
 * de quién es · a dónde va · cuánto · hasta cuándo vale.
 */

const MODE_ICON: Record<string, typeof Ship> = { SEA: Ship, AIR: Plane, LAND: Truck };

/** Colores del chip de vigencia. Ámbar avisa, rojo bloquea la promesa. */
const VALIDITY_STYLE: Record<QuoteValidityState, string> = {
  valid: 'bg-ok-suave text-ok ring-ok',
  expiring: 'bg-prixia-gold/15 text-prixia-ink ring-prixia-gold/40',
  expired: 'bg-bloqueo-suave text-bloqueo ring-bloqueo',
  unknown: 'bg-muted text-muted-foreground ring-border',
};

type FilterKey = 'all' | QuoteValidityState;

function money(value: number | null | undefined, currency = 'USD'): string {
  return new Intl.NumberFormat('es-BO', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value ?? 0);
}

const shortDate = (iso: string | null | undefined) => formatShortDate(iso);

export interface QuotesTableProps {
  quotes: ImportOrder[];
  isLoading: boolean;
  onOpen: (id: string) => void;
  onConvert: (quote: ImportOrder) => void;
  /** Algo cambió y hay que releer la lista. */
  onChanged: () => void;
  onRequote: (quoteId: string) => void;
  onDuplicate: (quote: ImportOrder) => void;
  /**
   * Filtro por estado comercial, gobernado desde afuera.
   *
   * Existe para que las tarjetas del embudo de arriba puedan filtrar esta tabla:
   * antes cada tarjeta era un número suelto y había que volver a filtrar acá a
   * mano, adivinando qué filtro correspondía a cuál.
   *
   * Si no se pasan, la tabla se maneja sola como siempre.
   */
  outcomeFilter?: QuoteOutcome | 'all';
  onOutcomeFilterChange?: (v: QuoteOutcome | 'all') => void;
}

export function QuotesTable({
  quotes,
  isLoading,
  onOpen,
  onConvert,
  onChanged,
  onRequote,
  onDuplicate,
  outcomeFilter: outcomeFilterProp,
  onOutcomeFilterChange,
}: QuotesTableProps) {
  const [filter, setFilter] = useState<FilterKey>('all');
  /**
   * Filtro por estado comercial, y buscador.
   *
   * Son ejes distintos y se combinan: la vigencia dice si el precio todavía se
   * puede prometer, el estado dice qué contestó el cliente. "Enviadas cuyo
   * precio ya venció" es una pregunta real —hay que recotizar y volver a
   * llamar— y con un solo filtro no se podía hacer.
   */
  const [outcomeInterno, setOutcomeInterno] = useState<QuoteOutcome | 'all'>('all');
  /*
    Controlado si le pasan el valor, y si no se maneja solo. Es el patrón que
    permite que las tarjetas de arriba filtren sin obligar a todos los que usan
    esta tabla a mantener ese estado.
  */
  const outcomeFilter = outcomeFilterProp ?? outcomeInterno;
  const setOutcomeFilter = (v: QuoteOutcome | 'all') => {
    if (onOutcomeFilterChange) onOutcomeFilterChange(v);
    else setOutcomeInterno(v);
  };
  const [search, setSearch] = useState('');

  /** Se calcula una vez la vigencia de cada una: la usan el filtro y la fila. */
  const withValidity = useMemo(
    () =>
      quotes.map((quote) => ({
        quote,
        // Se prefiere `quoteValidUntil`; `estimatedShipDate` es el respaldo de
        // las cotizaciones creadas antes de que existiera el campo propio.
        validity: quoteValidity(quote.quoteValidUntil ?? quote.estimatedShipDate),
      })),
    [quotes]
  );

  const counts = useMemo(() => {
    const acc: Record<FilterKey, number> = {
      all: withValidity.length,
      valid: 0,
      expiring: 0,
      expired: 0,
      unknown: 0,
    };
    for (const { validity } of withValidity) acc[validity.state]++;
    return acc;
  }, [withValidity]);

  /** Cuántas hay por estado, para no ofrecer filtros que dejan la tabla vacía. */
  const outcomeCounts = useMemo(() => {
    const acc = new Map<QuoteOutcome, number>();
    for (const { quote } of withValidity) {
      const o = effectiveOutcome(quote);
      acc.set(o, (acc.get(o) ?? 0) + 1);
    }
    return acc;
  }, [withValidity]);

  const visible = useMemo(() => {
    // Buscar por número o por cliente: son las dos formas en que alguien se
    // acuerda de una cotización. Sin acentos ni mayúsculas, porque nadie escribe
    // "Shanghái" con tilde cuando está apurado.
    const q = search.trim().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

    return withValidity.filter(({ quote, validity }) => {
      if (filter !== 'all' && validity.state !== filter) return false;
      if (outcomeFilter !== 'all' && effectiveOutcome(quote) !== outcomeFilter) return false;
      if (q === '') return true;

      const heno = [
        quote.orderNumber,
        quote.customer?.name,
        quote.originPort,
        quote.destinationPort,
      ]
        .filter(Boolean)
        .join(' ')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase();

      return heno.includes(q);
    });
  }, [withValidity, filter, outcomeFilter, search]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    );
  }

  if (quotes.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-14 text-center">
        <FileSpreadsheet className="h-9 w-9 text-prixia-silver" />
        <p className="text-sm font-medium">Todavía no cotizaste nada</p>
        <p className="max-w-sm text-xs text-muted-foreground">
          Andá al Cotizador, buscá una ruta y guardá la cotización. Va a aparecer acá con su
          vigencia y podés convertirla en orden cuando el cliente acepte.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/*
        Buscador y filtro por estado en la misma fila.

        El buscador va primero porque es lo que se usa cuando ya sabés qué
        buscás; los filtros, cuando estás explorando.
      */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por número, cliente o ruta…"
            className="h-8 pl-8 pr-8 text-xs"
            aria-label="Buscar cotizaciones"
          />
          {search !== '' && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Limpiar la búsqueda"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <select
          value={outcomeFilter}
          onChange={(e) => setOutcomeFilter(e.target.value as QuoteOutcome | 'all')}
          className="h-8 rounded-md border border-border bg-background px-2 text-xs"
          aria-label="Filtrar por estado"
        >
          <option value="all">Todos los estados</option>
          {/*
            Los CINCO estados, siempre. Los que no tienen ninguna, deshabilitados.

            🔴 Antes se escondían los estados en cero. La intención era no
            ofrecer un filtro que deja la tabla vacía, pero el efecto era peor:
            con estos datos solo aparecían "Borrador" y "Perdida", y la pantalla
            daba a entender que el sistema tiene dos estados. No se puede
            aprender que existe "Enviada" si nunca se ve hasta que alguien
            marque una — y nadie marca lo que no sabe que existe.

            Mostrarlos en gris con (0) enseña el circuito completo y sigue sin
            dejar elegir uno que no lleva a ningún lado.
          */}
          {(['DRAFT', 'SENT', 'ACCEPTED', 'LOST', 'NO_RESPONSE'] as QuoteOutcome[]).map((o) => {
            const n = outcomeCounts.get(o) ?? 0;
            return (
              <option key={o} value={o} disabled={n === 0}>
                {OUTCOME_LABEL[o]} ({n})
              </option>
            );
          })}
        </select>
      </div>

      {/* Filtros por vigencia — es el eje de esta pantalla */}
      <div className="flex flex-wrap gap-1.5">
        {(
          [
            ['all', 'Todas'],
            ['valid', VALIDITY_LABEL.valid],
            ['expiring', VALIDITY_LABEL.expiring],
            ['expired', VALIDITY_LABEL.expired],
          ] as Array<[FilterKey, string]>
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            disabled={counts[key] === 0 && key !== 'all'}
            aria-pressed={filter === key}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              filter === key
                ? 'border-prixia-ink bg-prixia-ink text-white'
                : 'border-border hover:border-prixia-silver',
              counts[key] === 0 && key !== 'all' && 'opacity-40'
            )}
          >
            {label}
            <span className="tabular-nums opacity-70">{counts[key]}</span>
          </button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="bg-muted/50">
            <tr className="border-b text-xs">
              <th className="px-3 py-2 text-left font-medium">N°</th>
              <th className="px-3 py-2 text-left font-medium">Para quién</th>
              <th className="px-3 py-2 text-left font-medium">Ruta</th>
              <th className="px-3 py-2 text-right font-medium">Flete cotizado</th>
              <th className="px-3 py-2 text-left font-medium">Vigencia</th>
              {/*
                Vigencia y estado son dos cosas distintas y van juntas a
                propósito: una cotización puede estar VENCIDA y ACEPTADA a la
                vez —el cliente dijo que sí sobre un precio que ya hay que
                recotizar— y esconder cualquiera de las dos deja a alguien
                decidiendo con medio cuadro.
              */}
              <th className="px-3 py-2 text-left font-medium">Estado</th>
              <th className="w-28 px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {visible.map(({ quote, validity }) => {
              const Icon = MODE_ICON[quote.transportMode] ?? Ship;
              const isOwn = !quote.customer;
              // Las cotizaciones guardadas antes de que existiera el
              // seguimiento no traen estado; se leen como borrador, que es lo
              // que eran.
              // El estado del embarque manda sobre el seguimiento anotado: ver
              // `effectiveOutcome`. Sin esto la fila y el tablero contaban distinto.
              const outcome = effectiveOutcome(quote);

              return (
                <tr
                  key={quote.id}
                  className="border-b transition-colors last:border-0 hover:bg-muted/30"
                >
                  <td className="px-3 py-2.5">
                    <button
                      type="button"
                      onClick={() => onOpen(quote.id)}
                      className="whitespace-nowrap font-mono text-xs font-semibold text-prixia-blue hover:underline"
                    >
                      {quote.orderNumber}
                    </button>

                    {/*
                      La cadena de recotizaciones.

                      Se guardaba de qué cotización sale cada una y no se
                      mostraba en ningún lado. Una ruta recotizada cuatro veces
                      antes de cerrarse cuenta algo —el cliente dudó, el precio
                      se movió— que cuatro cotizaciones sueltas no cuentan.

                      Va debajo del número y en chico: es contexto, no el dato
                      principal de la fila.
                    */}
                    {quote.quotedFrom && (
                      <button
                        type="button"
                        onClick={() => onOpen(quote.quotedFrom!.id)}
                        className="mt-0.5 block text-[10px] leading-tight text-muted-foreground hover:text-foreground hover:underline"
                        title={`Recotizada a partir de ${quote.quotedFrom.orderNumber}`}
                      >
                        ↳ viene de {quote.quotedFrom.orderNumber}
                      </button>
                    )}
                    {(quote._count?.requotes ?? 0) > 0 && (
                      <p className="mt-0.5 text-[10px] leading-tight text-muted-foreground">
                        recotizada {quote._count.requotes}{' '}
                        {quote._count.requotes === 1 ? 'vez' : 'veces'}
                      </p>
                    )}
                  </td>

                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      {isOwn ? (
                        <>
                          <User className="h-3 w-3 text-muted-foreground" />
                          <span className="text-muted-foreground">Importación propia</span>
                        </>
                      ) : (
                        <>
                          <Building2 className="h-3 w-3 text-muted-foreground" />
                          {quote.customer?.name}
                        </>
                      )}
                    </span>
                  </td>

                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      <Icon className="h-3 w-3 shrink-0 text-prixia-blue" />
                      {quote.originPort ?? '—'}
                      <span className="text-prixia-silver">→</span>
                      {quote.destinationPort ?? '—'}
                    </span>
                    {quote.shippingLine && (
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        {quote.shippingLine}
                      </p>
                    )}
                  </td>

                  <td className="px-3 py-2.5 text-right">
                    <span className="font-mono text-sm font-semibold tabular-nums">
                      {money(quote.totalFreight, quote.currency)}
                    </span>
                  </td>

                  <td className="px-3 py-2.5">
                    <span
                      className={cn(
                        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset',
                        VALIDITY_STYLE[validity.state]
                      )}
                    >
                      {validity.label}
                    </span>
                    {validity.state !== 'unknown' && (
                      <p className="mt-0.5 text-[10px] text-muted-foreground">
                        {shortDate(quote.quoteValidUntil ?? quote.estimatedShipDate)}
                      </p>
                    )}
                  </td>

                  <td className="px-3 py-2.5">
                    <div className="flex flex-col items-start gap-0.5">
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-[11px] font-medium',
                          OUTCOME_TONE[outcome]
                        )}
                      >
                        {OUTCOME_LABEL[outcome]}
                      </span>
                      {/*
                        El motivo va pegado al estado. Una lista de "Perdida"
                        sin el porqué obliga a abrir cada una para entender el
                        patrón, que es justo lo que el motivo venía a evitar.
                      */}
                      {outcome === 'LOST' && quote.quoteLostReason && (
                        <span className="text-[10px] leading-tight text-muted-foreground">
                          {LOST_REASON_LABEL[quote.quoteLostReason]}
                        </span>
                      )}
                    </div>
                  </td>

                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        title="Ver la cotización"
                        onClick={() => onOpen(quote.id)}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <QuotePdfButton orderId={quote.id} orderNumber={quote.orderNumber} />
                      {/*
                        "Convertir" queda como botón suelto y no dentro del menú:
                        es la acción que cierra la venta, y esconderla detrás de
                        tres puntos la trataría como una más entre seis.
                      */}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1 px-2 text-[11px]"
                        title="El cliente aceptó: convertir en orden"
                        onClick={() => onConvert(quote)}
                      >
                        Convertir
                        <ArrowRight className="h-3 w-3" />
                      </Button>
                      <QuoteActions
                        quoteId={quote.id}
                        quoteNumber={quote.orderNumber}
                        outcome={outcome}
                        onChanged={onChanged}
                        onRequote={() => onRequote(quote.id)}
                        onDuplicate={() => onDuplicate(quote)}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/*
          Con tres filtros que se combinan, "no hay cotizaciones" a secas deja a
          la persona sin saber cuál aflojar. Se dice qué se buscó y se ofrece
          limpiar todo de una.
        */}
        {visible.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <p className="text-xs text-muted-foreground">
              Ninguna cotización
              {search !== '' && (
                <>
                  {' '}
                  con <strong>{search}</strong>
                </>
              )}
              {outcomeFilter !== 'all' && <> en estado {OUTCOME_LABEL[outcomeFilter]}</>}
              {filter !== 'all' && (
                <> {VALIDITY_LABEL[filter as QuoteValidityState]?.toLowerCase()}</>
              )}
              .
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => {
                setSearch('');
                setOutcomeFilter('all');
                setFilter('all');
              }}
            >
              Quitar los filtros
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
